import { z } from "zod";

// --- Domain Analysis ---

export const domainFindingCategorySchema = z.enum([
  "stakeholder",
  "process",
  "tension",
  "edge_case",
  "terminology",
  "gap",
]);

export type DomainFindingCategory = z.infer<typeof domainFindingCategorySchema>;

export const domainFindingSchema = z.object({
  category: domainFindingCategorySchema,
  title: z.string(),
  description: z.string(),
  relevantChunkIds: z.array(z.string()),
  /** Human-readable citation labels, e.g. "[1] Arbetsmiljölagen — 6 kap. 7 §" */
  citations: z.array(z.string()).optional(),
});

export type DomainFinding = z.infer<typeof domainFindingSchema>;

export const sourceRefSchema = z.object({
  sourceId: z.string(),
  sourceName: z.string(),
});

export type SourceRef = z.infer<typeof sourceRefSchema>;

export const domainAnalysisSchema = z.object({
  lens: z.string(),
  summary: z.string(),
  findings: z.array(domainFindingSchema),
  suggestedLenses: z.array(z.string()),
  sourcesCited: z.array(sourceRefSchema),
});

export type DomainAnalysis = z.infer<typeof domainAnalysisSchema>;

// --- Knowledge Reference (shared across suggestions) ---

export const knowledgeRefSchema = z.object({
  sourceType: z.enum(["chunk"]),
  sourceId: z.string(),
  relevanceNote: z.string(),
});

export type KnowledgeRef = z.infer<typeof knowledgeRefSchema>;

// --- Persona Suggestions ---

export const personaSuggestionSchema = z.object({
  name: z.string(),
  role: z.string(),
  background: z.string(),
  rationale: z.string(),
});

export type PersonaSuggestionWithRationale = z.infer<
  typeof personaSuggestionSchema
>;

// --- Scenario Suggestions ---

export const personaHintSchema = z.object({
  role: z.string(),
  reasoning: z.string(),
});

export type PersonaHint = z.infer<typeof personaHintSchema>;

export const situationRoleSchema = z.object({
  role: z.string(),
  stake: z.string(),
});

export type SituationRole = z.infer<typeof situationRoleSchema>;

export const outlineSuggestionSchema = z.object({
  name: z.string(),
  tension: z.string().nullable(),
  narrative: z.string(),
  complexityLevel: z.enum(["simple", "moderate", "complex"]),
  situationRoles: z.array(situationRoleSchema),
  knowledgeRefs: z.array(knowledgeRefSchema),
});

export type OutlineSuggestion = z.infer<typeof outlineSuggestionSchema>;

// --- Feature Suggestions ---

export const featureSuggestionSchema = z.object({
  name: z.string(),
  description: z.string(),
  relevantPersonaIds: z.array(z.string()),
  relevantOutlineIds: z.array(z.string()),
  knowledgeRefs: z.array(knowledgeRefSchema),
  rationale: z.string(),
});

export type FeatureSuggestion = z.infer<typeof featureSuggestionSchema>;
